<template>
  <div class="maintenance-view">
    <AppHeader title="Maintenance Management" description="Manage and track equipment maintenance schedules" />
    
    <!-- Stats Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <StatsCard 
        title="Total Maintenance" 
        :value="maintenanceStore.maintenance.length" 
        icon="🛠️"
        trend="total"
        :trend-value="maintenanceStore.maintenance.length"
      />
      <StatsCard 
        title="Scheduled" 
        :value="maintenanceStore.scheduledMaintenance.length" 
        icon="📅"
        trend="scheduled"
        :trend-value="maintenanceStore.scheduledMaintenance.length"
      />
      <StatsCard 
        title="Repairs" 
        :value="maintenanceStore.repairMaintenance.length" 
        icon="🔧"
        trend="repairs"
        :trend-value="maintenanceStore.repairMaintenance.length"
      />
      <StatsCard 
        title="Total Cost" 
        :value="`$${maintenanceStore.totalMaintenanceCost.toLocaleString()}`" 
        icon="💰"
        trend="cost"
        :trend-value="maintenanceStore.totalMaintenanceCost"
      />
    </div>

    <!-- Quick Actions -->
    <div class="bg-white rounded-lg shadow-sm p-4 mb-6">
      <div class="flex flex-col sm:flex-row justify-between items-center gap-4">
        <h3 class="text-lg font-semibold text-gray-800">Quick Actions</h3>
        <div class="flex flex-wrap gap-2">
          <RouterLink to="/maintenance/create" class="btn-primary">
            <span class="mr-2">➕</span> Schedule Maintenance
          </RouterLink>
          <RouterLink to="/maintenance/schedule" class="btn-secondary">
            <span class="mr-2">📅</span> View Schedule
          </RouterLink>
          <button @click="exportMaintenanceData" class="btn-outline">
            <span class="mr-2">📊</span> Export Data
          </button>
        </div>
      </div>
    </div>

    <!-- Two-column layout -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main content -->
      <div class="lg:col-span-2">
        <MaintenanceList />
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Upcoming Maintenance -->
        <UpcomingMaintenance @view-all="navigateToSchedule" />

        <!-- Maintenance by Type Chart -->
        <div class="bg-white rounded-lg shadow-sm p-4">
          <h3 class="text-lg font-semibold mb-4">Maintenance by Type</h3>
          <div class="space-y-2">
            <div v-for="type in maintenanceTypes" :key="type.name" class="flex justify-between items-center">
              <div class="flex items-center">
                <span class="w-3 h-3 rounded-full mr-2" :class="type.color"></span>
                <span class="text-sm">{{ type.name }}</span>
              </div>
              <span class="text-sm font-medium">{{ type.count }}</span>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="bg-white rounded-lg shadow-sm p-4">
          <h3 class="text-lg font-semibold mb-4">Recent Activity</h3>
          <div v-if="recentMaintenance.length === 0" class="text-center text-gray-500 py-4">
            No recent activity
          </div>
          <div v-else class="space-y-3">
            <div v-for="item in recentMaintenance" :key="item.id" class="flex items-start">
              <div class="flex-shrink-0 mt-1">
                <span class="text-lg" :class="getMaintenanceIcon(item.maintenance_type)"></span>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-gray-900">{{ item.description }}</p>
                <p class="text-xs text-gray-500">
                  {{ formatDate(item.maintenance_date) }} • {{ item.equipment?.name }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Placeholder for empty maintenance records -->
    <div v-if="!maintenanceStore.maintenance || maintenanceStore.maintenance.length === 0" class="text-center py-12 bg-gray-50 rounded-lg mt-6">
      <p class="text-gray-500">No maintenance records found. Add your first record to get started.</p>
      <RouterLink to="/maintenance/create" class="btn-primary mt-4">Add Maintenance Record</RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMaintenanceStore } from '@/stores/maintenanceStore';
import AppHeader from '@/components/shared/AppHeader.vue';
import StatsCard from '@/components/shared/StatsCard.vue';
import MaintenanceList from '@/components/maintenance/MaintenanceList.vue';
import UpcomingMaintenance from '@/components/maintenance/UpcomingMaintenance.vue';
import { RouterLink } from 'vue-router';

const router = useRouter();
const maintenanceStore = useMaintenanceStore();

onMounted(() => {
  maintenanceStore.fetchMaintenance();
  maintenanceStore.fetchStats();
  maintenanceStore.fetchUpcoming(7);
});

const maintenanceTypes = computed(() => [
  { name: 'Scheduled', count: maintenanceStore.scheduledMaintenance.length, color: 'bg-blue-500' },
  { name: 'Repairs', count: maintenanceStore.repairMaintenance.length, color: 'bg-red-500' },
  { name: 'Inspections', count: maintenanceStore.inspectionMaintenance.length, color: 'bg-green-500' }
]);

const recentMaintenance = computed(() => {
  return [...maintenanceStore.maintenance]
    .sort((a, b) => new Date(b.maintenance_date).getTime() - new Date(a.maintenance_date).getTime())
    .slice(0, 5);
});

const navigateToSchedule = () => {
  router.push('/maintenance/schedule');
};

const exportMaintenanceData = () => {
  // Export functionality implementation
  console.log('Exporting maintenance data...');
};

const getMaintenanceIcon = (type: string) => {
  switch (type) {
    case 'scheduled': return '📅';
    case 'repair': return '🔧';
    case 'inspection': return '👀';
    default: return '🛠️';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString();
};
</script>

<style scoped>
.maintenance-view {
  min-height: calc(100vh - 120px);
}
</style>